import { useEffect, useRef, useState } from "react";
import { useTheme } from "../../../../context/useThemeContext";
import useDataStore from "../../../../stores/dataStore";

function NoteTitle(props: {
    noteID: string;
    isRenaming: boolean;
    setIsRenaming: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    // Hook
    const { theme } = useTheme();

    // Store
    const noteTitle = useDataStore(
        (state) => state.data.notes[props.noteID]?.title || "_"
    );

    // State for controlled input
    const [inputValue, setInputValue] = useState(noteTitle);

    // Update input value when noteTitle changes and not renaming
    useEffect(() => {
        if (!props.isRenaming) {
            setInputValue(noteTitle);
        }
    }, [noteTitle, props.isRenaming]);

    // Ref
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (props.isRenaming) inputRef.current?.focus();
        if (props.isRenaming && inputRef.current) {
            inputRef.current.select();
        }
    }, [props.isRenaming]);

    function SetTitle(
        event: React.FocusEvent<HTMLInputElement>,
        title: string
    ) {
        const e = event.currentTarget as HTMLInputElement;
        if (!props.noteID) return;

        const formattedTitle = title.trim().substring(0, 50);

        props.setIsRenaming(false);
        useDataStore
            .getState()
            .set.note(props.noteID, { title: formattedTitle }); // Remove spaces AND cut to 50 chars
        e.value = formattedTitle;
    }

    return (
        <input
            ref={inputRef}
            type="text"
            className="bg-transparent outline-none text-nowrap whitespace-nowrap text-ellipsis overflow-hidden rounded-[2px]"
            style={{
                minWidth: 0,
                width: "100%",
                color: theme.fontColor[50],
                lineHeight: "24px",
                minHeight: "24px",
                borderBottom: !props.isRenaming
                    ? "1px solid transparent"
                    : "1px solid " + theme.accentColor,
                cursor: props.isRenaming ? "text" : "unset !important",
            }}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onMouseDown={(e) => {
                if (!props.isRenaming) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onClick={(e) => {
                if (props.isRenaming) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    e.currentTarget.blur();
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    e.stopPropagation();
                    // Revert to start
                    setInputValue(noteTitle);
                    e.currentTarget.blur();
                }
            }}
            onDoubleClick={(e) => {
                e.stopPropagation();
                props.setIsRenaming(true);
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                window.getSelection()?.removeAllRanges();
                if (!props.noteID) return;
                props.setIsRenaming(false);
                if (inputValue === noteTitle) return;
                SetTitle(e, inputValue);
            }}
        />
    );
}

export default NoteTitle;